const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const round2 = (value) => Math.round(value * 100) / 100;

const STEPS = [1, 2, 5];

const candidateSteps = () => {
  const steps = [];
  for (let power = 0; power <= 9; power += 1) {
    const scale = Math.pow(10, power);
    STEPS.forEach((base) => steps.push(base * scale));
    if (power > 0) steps.push(2.5 * scale);
  }
  return steps.sort((a, b) => a - b);
};

export const integerTicks = (maxValue) => {
  const max = Number(maxValue);
  if (!Number.isFinite(max) || max <= 0) return [0, 1, 2, 3, 4];

  let chosen = null;
  candidateSteps().forEach((step) => {
    const intervals = Math.ceil(max / step);
    if (intervals < 3 || intervals > 5) return;
    const distance = Math.abs(intervals - 4);
    if (!chosen || distance < chosen.distance || (distance === chosen.distance && step > chosen.step)) {
      chosen = { step, intervals, distance };
    }
  });

  if (!chosen) return [0, 1, 2, 3, 4];

  const ticks = [];
  for (let i = 0; i <= chosen.intervals; i += 1) ticks.push(chosen.step * i);
  return ticks;
};

export const barLayout = ({ width, count, ratio = 0.58 }) => {
  if (!(width > 0) || !(count > 0)) return { slot: 0, barWidth: 0, bars: [] };

  const slot = round2(width / count);
  const barWidth = round2(slot * ratio);
  const offset = round2((slot - barWidth) / 2);
  const bars = [];

  for (let index = 0; index < count; index += 1) {
    bars.push({
      index,
      x: round2(index * slot + offset),
      center: round2(index * slot + slot / 2),
      width: barWidth,
    });
  }

  return { slot, barWidth, bars };
};

export const barPath = ({ x, y, width, height, radius = 4 }) => {
  if (!(height > 0) || !(width > 0)) return "";

  const r = round2(Math.min(radius, width / 2, height));
  const bottom = round2(y + height);
  const right = round2(x + width);
  const shoulder = round2(y + r);

  return [
    `M ${x} ${bottom}`,
    `L ${x} ${shoulder}`,
    `Q ${x} ${y} ${round2(x + r)} ${y}`,
    `L ${round2(right - r)} ${y}`,
    `Q ${right} ${y} ${right} ${shoulder}`,
    `L ${right} ${bottom}`,
    "Z",
  ].join(" ");
};

export const labelStride = (count, plotWidth, minLabelWidth = 46) => {
  if (!(count > 0) || !(plotWidth > 0)) return 1;
  return Math.max(1, Math.ceil((minLabelWidth * count) / plotWidth));
};

const parts = (iso) => {
  const [year, month, day] = String(iso || "").split("-").map(Number);
  if (!Number.isFinite(day) || !MONTHS[month - 1]) return null;
  return { year, month: MONTHS[month - 1], day };
};

export const dayMonth = (iso) => {
  const value = parts(iso);
  return value ? `${value.day} ${value.month}` : "";
};

export const rangeSubtitle = (fromIso, toIso) => {
  const from = parts(fromIso);
  const to = parts(toIso);
  if (!from || !to) return "";
  if (from.day === to.day && from.month === to.month) return `${to.day} ${to.month}`;
  if (from.month === to.month && from.year === to.year) return `${from.day} – ${to.day} ${to.month}`;
  return `${from.day} ${from.month} – ${to.day} ${to.month}`;
};

export const computeSplinePath = (points, baseline, topY) => {
  if (!points || points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;
  const tension = 0.35;

  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    const dx = p2.x - p1.x;

    let m1 = (p2.y - p0.y) / (p2.x - p0.x || 1);
    let m2 = (p3.y - p1.y) / (p3.x - p1.x || 1);

    if (i > 0 && (p1.y - p0.y) * (p2.y - p1.y) <= 0) m1 = 0;
    if (i < points.length - 2 && (p2.y - p1.y) * (p3.y - p2.y) <= 0) m2 = 0;
    if (p1.y === baseline) m1 = 0;
    if (p2.y === baseline) m2 = 0;

    let cp1x = Math.round((p1.x + dx * tension) * 10) / 10;
    let cp1y = Math.round((p1.y + m1 * dx * tension) * 10) / 10;
    let cp2x = Math.round((p2.x - dx * tension) * 10) / 10;
    let cp2y = Math.round((p2.y - m2 * dx * tension) * 10) / 10;

    if (baseline !== undefined) {
      cp1y = Math.min(baseline, cp1y);
      cp2y = Math.min(baseline, cp2y);
    }
    if (topY !== undefined) {
      cp1y = Math.max(topY, cp1y);
      cp2y = Math.max(topY, cp2y);
    }

    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
};
